import { makeParentAsyncStorage, ParentAndChildrenWithIds, ParentAndOptionalChild, ParentChildAsyncStorageConfig, RandomIdAndTimeService } from "@me8eon/async_storage/src/parent.child.async.storage";
import { UserData } from "@me8eon/authentication";
import { AiSessionData, MessageWithActionAndData, sessionsAndMessagesDebugName } from "./messages.sessions";
import { AsyncStore, defaultRandomId, makeAsyncStoreAndGet } from "@me8eon/async_storage";
import { IStorage } from "@me8eon/storage";
import { jsonCodec } from "@me8eon/codec";
import { ErrorsOr, isErrors, valueOrDefault } from "@me8eon/errors";
import { makeContextForParentChildAsyncState } from "@me8eon/react_storage/src/react.parent.child.async.storage";
import { GetterSetter, makeUseStateChild, Setter } from "@me8eon/context";
import { useCallback, useContext, useEffect, useRef } from "react";
import { dateTimeService, TimeService } from "@me8eon/time";
import { isDataAs, isErrorAs, isLoadingAs } from "@me8eon/async";
import { useDebug } from "@me8eon/debug";
import { isValidJson } from "@me8eon/string_utils";


export const SessionsAndMessageConfig = (parentStore: AsyncStore<string, AiSessionData>,
    childStore: AsyncStore<string, MessageWithActionAndData[]>): ParentChildAsyncStorageConfig<string, AiSessionData, string, MessageWithActionAndData[]> =>
({
    childId: (parent: AiSessionData) => parent.selected,
    parentStore,
    childStore,
    newChildForNewParent: (parent: AiSessionData) => {
        const selectedSession = parent.sessions.find(s => s.id === parent.selected);
        const selectedSessionName = selectedSession?.name;
        return selectedSessionName //Does the parent have a selected session? If so, use its name as the first message
            ? { child: [{ content: selectedSessionName, role: "user" }], childId: parent.selected }
            : { child: [], childId: parent.selected };

    },
    makeNewParentWithDeletedChildIds: (parent: AiSessionData, deletedChildIds: string[], { random, timeService }): ParentAndOptionalChild<AiSessionData, string, MessageWithActionAndData[]> => {
        const sessions = parent.sessions.filter(s => !deletedChildIds.includes(s.id));
        if (sessions.length == 0) {
            const childId = random();
            const newSession = { name: "", time: timeService().toISOString(), id: childId };
            return {
                parent: { ...parent, sessions: [newSession], selected: childId },
                childAndId: { childId, child: [] },
            };
        }
        const currentSelectionValid = sessions.find(s => s.id === parent.selected);
        return currentSelectionValid
            ? { parent: { ...parent, sessions, selected: parent.selected }, childAndId: undefined }
            : { parent: { ...parent, sessions, selected: sessions[0].id }, childAndId: undefined };
    },
});

const initialSessionStateForUser = ({ random, timeService }: RandomIdAndTimeService) => (): ErrorsOr<AiSessionData> => {
    const selected = random();
    const time = timeService().toISOString();
    const value: AiSessionData = { selected, sessions: [{ id: selected, name: "", time }] };
    return { value };
};

export type SessionMessagesStoreDetails = {
    sessionStore: AsyncStore<UserData, AiSessionData>
    messagesStore: AsyncStore<string, MessageWithActionAndData[]>
    messageAndSessionsStore: ParentChildAsyncStorageConfig<UserData, AiSessionData, string, MessageWithActionAndData[]>
}

export type MessageAndSessionsStoreFromIstorageConfig = RandomIdAndTimeService & {
    sessionStorage: IStorage
    messagesStorage: IStorage
}

export function makeMessageAndSessionsStoreFromIStorage(makeConfig: MessageAndSessionsStoreFromIstorageConfig) {
    const { sessionStorage, messagesStorage, ...randomAndTime } = makeConfig;
    const sessionStore: AsyncStore<string, AiSessionData> = makeAsyncStoreAndGet<string, AiSessionData>(sessionStorage,
        jsonCodec("session"), email => `sessions-${email}`, initialSessionStateForUser(randomAndTime));
    const messagesStore: AsyncStore<string, MessageWithActionAndData[]> = makeAsyncStoreAndGet<string, MessageWithActionAndData[]>(messagesStorage,
        jsonCodec("messages"), e => `session-${e}`, () => ({ value: [] }));

    const config =
        SessionsAndMessageConfig(sessionStore, messagesStore);

    const messageAndSessionsStore = makeParentAsyncStorage(config);
    return { sessionStore, messagesStore, messageAndSessionsStore };
}

export type MessageAndSessionsStoreFromAsyncStorageConfig = RandomIdAndTimeService & {
    sessionStore: AsyncStore<string, AiSessionData>
    messagesStore: AsyncStore<string, MessageWithActionAndData[]>
}

export function makeMessageAndSessionsStoreFromAsyncStorage(makeConfig: MessageAndSessionsStoreFromAsyncStorageConfig) {
    const { sessionStore, messagesStore, ...randomAndTime } = makeConfig;

    const config =
        SessionsAndMessageConfig(sessionStore, messagesStore);

    const messageAndSessionsStore = makeParentAsyncStorage(config);
    return { sessionStorage: sessionStore, messagesStorage: messagesStore, messageAndSessionsStore };
}


export const { ops: useSessionAndMessageOps, useAsync: useAsyncSessionAndMessages, use: useSessionAndMessages, Provider: SessionAndMessagesProvider, context: SessionAndMessagesContext } =
    makeContextForParentChildAsyncState<"sessionsAndMessages", string, AiSessionData, string, MessageWithActionAndData[]>("sessionsAndMessages", { debug: true });

// export const useAiSessionIdOps = makeUseStateChild(useSessionAndMessages,
//     lb => lb.focusOn("parent").focusOn("selected"));


export function useAiSessionIdOps(): GetterSetter<string> {
    const contextValue = useContext(SessionAndMessagesContext);
    if (!contextValue) throw new Error(`useAiSessionIdOps must be called inside a SessionAndMessagesProvider`);
    const { ops, store, parentChildOps } = contextValue;
    const [state, setState] = parentChildOps;
    const requestVersion = useRef(0);
    const id = isDataAs(state) && state.data.parent.selected;
    const setId: Setter<string> = useCallback((theId) => {
        const newId = typeof theId === "string" ? theId : (theId as any)(id);
        const thisRequest = ++requestVersion.current;
        if (!isDataAs(state)) throw new Error(`useAiSessionIdOps called when not data. ${state}`);
        store.getChild(newId).then(errorOrNewChild => {
            if (thisRequest !== requestVersion.current) return; // Ignore if the request is outdated
            let newChild = valueOrDefault(errorOrNewChild, []);
            newChild = newChild.filter((item) => item.action !== "quick-actions");
            const newParent: AiSessionData = {
                ...state.data.parent,
                selected: newId,
            };
            const newData = { parent: newParent, parentId: state.data.parentId, childId: newId, child: newChild };
            setState({ data: newData });
        });
    }, [state, store]);
    if (!isDataAs(state)) return ["", () => { }];
    return [id as string, setId];
}

export const useJustSessionOps = makeUseStateChild(useSessionAndMessages,
    lb => lb.focusOn("parent"));

export type useMessagesConfig = {
    timeService: TimeService
}

const defaultUseMessageConfig: useMessagesConfig = { timeService: dateTimeService };

/** Provides access to the messages, but also will update the name if needed */
export function useMessages(config = defaultUseMessageConfig): GetterSetter<MessageWithActionAndData[]> {
    const [sm, setSm] = useSessionAndMessages();
    const messages = sm?.child || [];
    const contextValue = useContext(SessionAndMessagesContext);
    if (!contextValue) throw new Error(`useMessages must be called inside a SessionAndMessagesProvider`);
    const { store } = contextValue;
    const setMessages: Setter<MessageWithActionAndData[]> = useCallback(async (newMessages) => {
        const result = await store.getParentAndChild(sm.parentId, false)
        let latestCheck = null;
        if(!isErrors(result)) {
            latestCheck = result.value;
        }
        setSm(oldSm => {
            const prevSessions = oldSm?.parent?.sessions || [];
            const latestSessions = latestCheck?.parent.sessions || [];
            const sessionMap = new Map(prevSessions.map(s => [s.id, s]));
            latestSessions.forEach(s => sessionMap.set(s.id, s));
            const updatedSessions = Array.from(sessionMap.values());
            const prevMessages = oldSm?.child || [];
            const resultMessages = typeof newMessages === "function" ? newMessages(prevMessages) : newMessages;
            const currentSession = updatedSessions.find?.(s => s.id === oldSm?.parent?.selected);
            const currentName = currentSession?.name ?? "";
            if (currentName === "") {
                // Use the last user message as a fallback session name
                const lastName = resultMessages.reduce<string>((acc, m) => {
                    if (m.role !== "user") return acc;
                    return typeof m.content === "string" ? m.content : acc;
                }, "");
                const sessions = updatedSessions.map(s => s.id === oldSm.parent.selected ? { ...s, name: lastName, time: config.timeService().toISOString() } : s).sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
                return { ...oldSm, parent: { ...oldSm.parent, sessions: sessions }, child: resultMessages };
            } else {
                const sessions = updatedSessions.map(s => s.id === oldSm.parent.selected ? { ...s, time: config.timeService().toISOString() } : s).sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
                return { ...oldSm, child: resultMessages, parent: { ...oldSm.parent, sessions} };
            }
        });
    }, [setSm]);
    return [messages, setMessages];
}

export type MakeNewSessionConfig = {
    random: () => string
    timeService: TimeService
}
export const defaultMakeNewSessionConfig: MakeNewSessionConfig = { random: defaultRandomId, timeService: dateTimeService };
export type MakeNewSessionFn = (name: string) => Promise<void>

export function useMakeNewSession(config: MakeNewSessionConfig = defaultMakeNewSessionConfig): MakeNewSessionFn {
    const [sessions, setSessions] = useAsyncSessionAndMessages();
    const debug = useDebug(sessionsAndMessagesDebugName);
    const sessionsRef = useRef(sessions);
    useEffect(() => {
        sessionsRef.current = sessions;
    }, [sessions]);
    return useCallback(async name => {
        // Waiting for sessions.loading to finish before proceeding
        while (isLoadingAs(sessionsRef.current)) {
            await new Promise(resolve => setTimeout(resolve, 20));
        }
        const currentSessions = sessionsRef.current;
        if (isErrorAs(currentSessions)) throw new Error(`useMakeNewSession called when error. ${JSON.stringify(currentSessions)}`);
        if (!isDataAs(currentSessions)) throw new Error(`useMakeNewSession called when not data. ${JSON.stringify(currentSessions)}`);
        const { parent: session } = currentSessions.data;
        let parsedMessages: MessageWithActionAndData[] = [];
        if (isValidJson(name)) {
            parsedMessages = parseMessagesFromName(name);
            if (parsedMessages.length > 0) {
                name = parsedMessages[0].content;
            } else {
                name = "";
            }
        }

        const newMessages = buildNewMessages(name, parsedMessages);
        const needsNewId = currentSessions.data.child.length > 0 || parsedMessages.length > 0 || newMessages.length > 0;
        if (!needsNewId) return;
        const newTime = config.timeService().toISOString();
        // If there are existing messages, we need to create a new session. But if the messages are empty, we can just stay with the existing session
        const sessionWithNoName = session.sessions.find(s => s.name === "");

        const { updatedSession, child } = sessionWithNoName ? updateExistingSession(session, name, newTime, sessionWithNoName.id) : createNewSession(session, name, newTime, config.random());
        debug("useMakeNewSession: setSessions called with", "old sessions:", currentSessions, "new sessions:", { ...currentSessions.data, parent: updatedSession, childId: child, child: newMessages });
        const data: ParentAndChildrenWithIds<string, AiSessionData, string, MessageWithActionAndData[]> =
            { ...currentSessions.data, parent: updatedSession, childId: child, child: newMessages };
        setSessions({ data });
    }, [config, setSessions, debug]);

}

function isValidMessage(message: any): message is MessageWithActionAndData {
    return message && typeof message.content === "string" && typeof message.role === "string";
}

function parseMessagesFromName(name: string): MessageWithActionAndData[] {
    try {
        const parsed = JSON.parse(name);
        if (!Array.isArray(parsed) || parsed.length === 0) {
            return [];
        }
        const allValid = parsed.every(isValidMessage);
        return allValid ? parsed : [];
    } catch (e) {
        return [];
    }
}

function buildNewMessages(name: string, parsedMessages: MessageWithActionAndData[]): MessageWithActionAndData[] {
    if (parsedMessages.length > 0) {
        return parsedMessages;
    }
    return name === "" ? [] : [{ content: name, role: "user", data: [], action: "user-query" }];
}

function createNewSession(session: AiSessionData, name: string, time: string, newId: string) {
    const updatedSession: AiSessionData = {
        ...session,
        selected: newId,
        sessions: [...session.sessions, { id: newId, name, time }],
    };
    return { updatedSession, child: newId };
}

function updateExistingSession(session: AiSessionData, name: string, time: string, sessionId: string) {
    const updatedSessions = session.sessions.map(s => s.id === sessionId ? { ...s, name, time } : s);
    return {
        updatedSession: { ...session, sessions: updatedSessions, selected: sessionId },
        child: sessionId,
    };
}
